/* eslint-disable react-hooks/exhaustive-deps */
import {
  useMediaQuery,
  Grid,
  Box,
  useTheme,
  Typography,
  Button,
  Container,
  Skeleton,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { BusinessSectorStructure, CompanyTypeTaxSystemEstructure, TaxSystemConfigResponse } from "@interfaces/index";
import { amountToText, calculateAmountMonthly, formatCurrency } from "@helpers/funciones";
import { StyledSlider } from "@components/CustomSlider";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import IconCheckCotizador from "@components/icons/IconCheckCotizador";
import {
  useLazyGetBusinessSectorByTaxCodeQuery,
  useLazyGetCompanyTypeConfigQuery,
  useLazyGetLoanUseCategoriesConfigQuery,
} from "@api/catalogs";

const TAX_COMPANYS = "CVNSC";

const TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PM = "SFI01";
const TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PFAE = "SFI02";
const TAXSYSTEM_SFI_HABILITACION_Y_AVIO_PM = "SFI03";
const TAXSYSTEM_SFI_HABILITACION_Y_AVIO_PFAE = "SFI04";

type MockOption = {
  value: string;
  label: string;
};

type MockProduct = {
  value: string;
  label: string;
  creditTypeLabel: string;
};

type CompanyTypeKey = "agroindustria" | "pyme" | "institucion";

type CompanyOption = {
  id: string;
  value: CompanyTypeKey;
  label: string;
};

const COMPANY_TYPE_BY_CODE: Record<string, CompanyTypeKey> = {
  AGRO: "agroindustria",
  PYME: "pyme",
  IFIN: "institucion",
};

const DESTINATION_OPTIONS: Record<string, MockOption[]> = {
  agroindustria: [
    { value: "costos_operacion", label: "Costos de Operación" },
    { value: "cuentas_por_cobrar", label: "Cuentas por Cobrar" },
    { value: "produccion_cultivo", label: "Establecimiento y producción de cultivo" },
    { value: "activo_fijo", label: "Adquisición Activo Fijo" },
    { value: "insumos", label: "Insumos e inventarios" },
  ],
  pyme: [
    { value: "capital_trabajo", label: "Capital de trabajo" },
    { value: "expansion", label: "Expansión operativa" },
    { value: "inventario", label: "Compra de inventario" },
    { value: "equipo", label: "Adquisición de equipo" },
  ],
  institucion: [
    { value: "fondeo", label: "Fondeo" },
    { value: "cartera", label: "Compra de cartera" },
    { value: "liquidez", label: "Liquidez" },
    { value: "expansion_institucional", label: "Expansión institucional" },
  ],
};

const PRODUCT_OPTIONS: MockProduct[] = [
  {
    value: "linea_credito",
    label: "Capital de Trabajo - Línea de Crédito",
    creditTypeLabel: "Revolvente",
  },
  {
    value: "avio",
    label: "Habilitación y/o Avío",
    creditTypeLabel: "Simple",
  },
];

const TAX_CONFIG_BY_SELECTION = {
  PM: {
    linea_credito: "configTrabajoPM",
    avio: "configAvioPM",
  },
  PFAE: {
    linea_credito: "configTrabajoPFAE",
    avio: "configAvioPFAE",
  },
} as const;

const DEFAULT_COMPANY_TYPE: CompanyTypeKey = "agroindustria";
const DEFAULT_PRODUCT = PRODUCT_OPTIONS[0].value;

const capitalizeWords = (value: string) =>
  value
    .split(" ")
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word))
    .join(" ");

const CotizadorSfi = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getCompanyTypeConfig] = useLazyGetCompanyTypeConfigQuery();
  const [getBusinessSectorByTaxCode] = useLazyGetBusinessSectorByTaxCodeQuery();
  const [getLoanUseCategoriesConfig] = useLazyGetLoanUseCategoriesConfigQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [configTrabajoPM, setConfigTrabajoPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configTrabajoPFAE, setConfigTrabajoPFAE] = useState<TaxSystemConfigResponse | null>(null);
  const [configAvioPM, setConfigAvioPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configAvioPFAE, setConfigAvioPFAE] = useState<TaxSystemConfigResponse | null>(null);

  const [personType, setPersonType] = useState<string>("PM");
  const [companyType, setCompanyType] = useState<CompanyTypeKey>(DEFAULT_COMPANY_TYPE);
  const [selectedSector, setSelectedSector] = useState<string>("");
  const [selectedDestination, setSelectedDestination] = useState<string>(
    DESTINATION_OPTIONS[DEFAULT_COMPANY_TYPE][2].value,
  );
  const [selectedProduct, setSelectedProduct] = useState<string>(DEFAULT_PRODUCT);

  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);
  const [companyTypes, setCompanyTypes] = useState<CompanyTypeTaxSystemEstructure[]>([]);
  const [businessSectors, setBusinessSectors] = useState<BusinessSectorStructure[]>([]);

  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
  });

  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [
          responseTrabajoPM,
          responseTrabajoPFAE,
          responseAvioPM,
          responseAvioPFAE,
          responseCompanyConfig,
          responseBusinessSector,
          responseLoanUseCategories,
        ] = await Promise.all([
          getTaxSystemConfig(TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PM).unwrap(),
          getTaxSystemConfig(TAXSYSTEM_SFI_CAPITAL_DE_TRABAJO_PFAE).unwrap(),
          getTaxSystemConfig(TAXSYSTEM_SFI_HABILITACION_Y_AVIO_PM).unwrap(),
          getTaxSystemConfig(TAXSYSTEM_SFI_HABILITACION_Y_AVIO_PFAE).unwrap(),
          getCompanyTypeConfig({ taxcode: TAX_COMPANYS }).unwrap(),
          getBusinessSectorByTaxCode({ taxcode: TAX_COMPANYS }).unwrap(),
          getLoanUseCategoriesConfig({ taxCode: TAX_COMPANYS }).unwrap(),
        ]);
        const activeCompanyTypes = responseCompanyConfig
          .filter((companyTypeOption) => companyTypeOption.active)
          .sort((left, right) => left.order - right.order);

        setConfigTrabajoPM(responseTrabajoPM);
        setConfigTrabajoPFAE(responseTrabajoPFAE);
        setConfigAvioPM(responseAvioPM);
        setConfigAvioPFAE(responseAvioPFAE);

        setCompanyTypes(activeCompanyTypes);
        console.log(responseLoanUseCategories);

        const orderedBusinessSectors = [...responseBusinessSector].sort((left, right) => left.order - right.order);

        setBusinessSectors(orderedBusinessSectors);

        if (orderedBusinessSectors.length > 0) {
          setSelectedSector(orderedBusinessSectors[0].id);
        }

        const firstAvailableCompanyType = activeCompanyTypes.find(
          (companyTypeOption) => COMPANY_TYPE_BY_CODE[companyTypeOption.company_type.code],
        );

        if (firstAvailableCompanyType) {
          setCompanyType(COMPANY_TYPE_BY_CODE[firstAvailableCompanyType.company_type.code]);
        }
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const configBySelection = {
      configTrabajoPM,
      configTrabajoPFAE,
      configAvioPM,
      configAvioPFAE,
    } as const;
    const configKey =
      TAX_CONFIG_BY_SELECTION[personType as keyof typeof TAX_CONFIG_BY_SELECTION]?.[
        selectedProduct as keyof (typeof TAX_CONFIG_BY_SELECTION)[keyof typeof TAX_CONFIG_BY_SELECTION]
      ];
    const nextConfig = configKey ? configBySelection[configKey] : null;

    if (nextConfig) {
      setCotizadorConfig(nextConfig);
    }
  }, [personType, selectedProduct, configTrabajoPM, configTrabajoPFAE, configAvioPM, configAvioPFAE]);

  useEffect(() => {
    const { minAmount, maxAmount, stepAmount, minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } =
      cotizadorConfig;

    if (minAmount < maxAmount && stepAmount > 0) {
      const marks = [];

      marks.push({ value: minAmount, label: amountToText(minAmount) });
      marks.push({ value: maxAmount, label: amountToText(maxAmount) });

      setAmountMarks(marks);
    }

    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];

      for (let i = minTerm; i <= maxTerm; i += stepTerm) {
        marks.push({ value: i, label: `${i}` });
      }

      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setSelectedTerm(defaultTerm);
    }
  }, [cotizadorConfig]);

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      const monthly = calculateAmountMonthly(
        selectedAmount,
        selectedTerm,
        interfaceConfig?.interest_rate,
        interfaceConfig?.comision,
        interfaceConfig?.iva,
      );
      setMonthlyRate(monthly);
    }
  }, [selectedAmount, selectedTerm, interfaceConfig?.interest_rate, interfaceConfig?.comision, interfaceConfig?.iva]);

  useEffect(() => {
    const destinations = DESTINATION_OPTIONS[companyType];

    setSelectedDestination(destinations[Math.min(2, destinations.length - 1)].value);
  }, [companyType]);

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate) {
      return;
    }
    const sourceCode = sessionStorage.getItem("source_code");

    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const companyOptions: CompanyOption[] = companyTypes.reduce<CompanyOption[]>((options, option) => {
    const companyTypeValue = COMPANY_TYPE_BY_CODE[option.company_type.code];

    if (!companyTypeValue) {
      return options;
    }

    options.push({
      id: option.id,
      value: companyTypeValue,
      label: option.company_type.name,
    });

    return options;
  }, []);
  const orderedCompanyOptions = companyOptions.sort((left, right) => {
    const leftOrder = companyTypes.find((option) => option.id === left.id)?.order ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = companyTypes.find((option) => option.id === right.id)?.order ?? Number.MAX_SAFE_INTEGER;

    return leftOrder - rightOrder;
  });
  const sectorOptions: MockOption[] = businessSectors.map((sector) => ({
    value: sector.id,
    label: sector.name,
  }));
  const destinationOptions = DESTINATION_OPTIONS[companyType];
  const productSelected = PRODUCT_OPTIONS.find((option) => option.value === selectedProduct) || PRODUCT_OPTIONS[0];
  const annualRateLabel = interfaceConfig?.interest_rate
    ? `TIIE + ${Number(interfaceConfig.interest_rate).toFixed(2)}%`
    : "-";
  const amountSummary = capitalizeWords(amountToText(selectedAmount || 0));
  const sharedButtonProps = {
    isMovile: isMobileOrTablet,
    icon: <IconCheckCotizador backgroundColor={theme?.palette?.primary.accent} width={22} height={22} />,
    width_icon: "22px",
    height_icon: "22px",
    top_icon: "-12px",
    font_size: "12px",
    font_size_movil: "11px",
    font_weight: 600,
  };
  const buttonGridItemSx = {
    display: "flex",
    justifyContent: "center",
    "& > *": {
      width: isMobileOrTablet ? "100%" : "auto",
    },
  };

  const renderSelect = (
    label: string,
    value: string,
    options: MockOption[],
    onChange: (event: SelectChangeEvent<string>) => void,
  ) => (
    <Box>
      <Typography variant="body1" fontWeight={600} color="primary.dark" textAlign="center" mb={1.5}>
        {label}
      </Typography>
      <FormControl fullWidth>
        <Select
          value={value}
          onChange={onChange}
          displayEmpty
          sx={{
            backgroundColor: "common.white",
            borderRadius: "16px",
            minHeight: 46,
            boxShadow: "0px 10px 30px rgba(5, 87, 53, 0.06)",
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: "#D8E6DF",
            },
            ".MuiSelect-select": {
              py: 1.75,
              px: 2.25,
              color: "primary.dark",
              fontSize: "15px",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
                borderRadius: "16px",
                boxShadow: "0px 18px 35px rgba(5, 87, 53, 0.10)",
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <Container maxWidth={"sm"}>
            <Typography variant="tofu_title" mt={4}>
              Cotizador
            </Typography>

            <Typography variant="body1" color="primary.dark" textAlign="center" mt={1} mb={3}>
              Simula tu crédito y conoce tu pago mensual estimado.
            </Typography>

            <Box pb={4}>
              <Box>
                <Typography variant="body1" fontWeight={600} color="primary.dark" textAlign="center" mb={2}>
                  Tipo de Empresa
                </Typography>

                <Grid container spacing={2} mb={4}>
                  {orderedCompanyOptions.map((option) => (
                    <Grid item xs={4} sm={4} key={option.id} sx={buttonGridItemSx}>
                      <ButtonTypeSelectedCustom
                        title={option.label}
                        onClick={() => setCompanyType(option.value)}
                        isSelected={companyType === option.value}
                        width="174px"
                        widthMoviles="100%"
                        height="62px"
                        heightMoviles="62px"
                        padding={isMobileOrTablet ? "12px 16px" : "12px"}
                        {...sharedButtonProps}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box display="grid" gap={3} mb={4}>
                {!isLoading ? (
                  <>
                    {renderSelect("Sector", selectedSector, sectorOptions, (event) =>
                      setSelectedSector(event.target.value),
                    )}
                  </>
                ) : (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width={isMobileOrTablet ? "100%" : 264}
                    height={60}
                  />
                )}

                {!isLoading ? (
                  <>
                    {renderSelect("Destino", selectedDestination, destinationOptions, (event) =>
                      setSelectedDestination(event.target.value),
                    )}
                  </>
                ) : (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width={isMobileOrTablet ? "100%" : 264}
                    height={60}
                  />
                )}
              </Box>

              <Box>
                <Typography variant="body1" fontWeight={600} color="primary.dark" textAlign="center" mb={2}>
                  Producto
                </Typography>
                {!isLoading ? (
                  <Grid container spacing={2} mb={4}>
                    {PRODUCT_OPTIONS.map((option) => (
                      <Grid item xs={6} sm={6} key={option.value} sx={buttonGridItemSx}>
                        <ButtonTypeSelectedCustom
                          title={option.label}
                          onClick={() => setSelectedProduct(option.value)}
                          isSelected={selectedProduct === option.value}
                          width="264px"
                          widthMoviles="100%"
                          height="60px"
                          heightMoviles="60px"
                          padding={isMobileOrTablet ? "12px 16px" : "12px"}
                          {...sharedButtonProps}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container spacing={2} mb={4}>
                    <Grid item xs={6} sm={6} display="flex" justifyContent="center">
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? "100%" : 264}
                        height={60}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} display="flex" justifyContent="center">
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? "100%" : 264}
                        height={60}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>

              <Box>
                <Typography variant="body1" fontWeight={600} color="primary.dark" textAlign="center" mb={2}>
                  Selecciona tu régimen fiscal
                </Typography>

                <Grid container spacing={2} mt={1}>
                  <Grid item xs={6} sm={6} sx={buttonGridItemSx}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Persona Moral"
                        onClick={() => setPersonType("PM")}
                        isSelected={personType === "PM"}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"264px"}
                        widthMoviles="100%"
                        padding={isMobileOrTablet ? "12px 16px" : "12px"}
                        {...sharedButtonProps}
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? "100%" : 264}
                        height={60}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} sm={6} sx={buttonGridItemSx}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Persona física con actividad empresarial"
                        onClick={() => setPersonType("PFAE")}
                        isSelected={personType === "PFAE"}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"264px"}
                        widthMoviles="100%"
                        padding={isMobileOrTablet ? "12px 16px" : "12px"}
                        {...sharedButtonProps}
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? "100%" : 264}
                        height={60}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box mt={5} ml={isMobileOrTablet ? 1 : 1} mr={isMobileOrTablet ? 1 : 1}>
                <Typography fontWeight={500} fontSize={"16px"} color="primary.dark" textAlign={"center"}>
                  Selecciona el monto
                </Typography>

                <Typography fontWeight={700} fontSize={"13px"} mt={1} color="primary.dark" textAlign={"center"}>
                  {amountToText(selectedAmount)}
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="amount"
                    min={cotizadorConfig.minAmount}
                    max={cotizadorConfig.maxAmount}
                    step={cotizadorConfig.stepAmount}
                    marks={amountMarks}
                    defaultValue={cotizadorConfig.defaultAmount}
                    value={selectedAmount}
                    onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} mt={3} ml={isMobileOrTablet ? 1 : 1} mr={isMobileOrTablet ? 1 : 1}>
                <Typography fontWeight={500} fontSize={"16px"} mb={2} color="primary.dark" textAlign={"center"}>
                  Selecciona el plazo en meses
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="term"
                    min={cotizadorConfig.minTerm}
                    max={cotizadorConfig.maxTerm}
                    step={1}
                    defaultValue={cotizadorConfig.defaultTerm}
                    marks={termMarks}
                    value={selectedTerm}
                    onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box
                mt={4}
                mb={3}
                p={isMobileOrTablet ? 2.5 : 3}
                sx={{
                  background: "linear-gradient(180deg, #F9FFFC 0%, #F3FBF7 100%)",
                  border: "1px solid #D8E6DF",
                  borderRadius: "18px",
                  boxShadow: "0px 16px 40px rgba(5, 87, 53, 0.06)",
                }}
              >
                <Typography variant="body1" fontWeight={700} color="primary.dark" mb={2.5}>
                  RESUMEN DE TU CRÉDITO
                </Typography>

                <Box display="grid" gap={1.5}>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="primary.dark">
                      Producto
                    </Typography>
                    <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                      {productSelected.label}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="primary.dark">
                      Monto
                    </Typography>
                    <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                      {amountSummary}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="primary.dark">
                      Plazo
                    </Typography>
                    <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                      {selectedTerm} meses
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="primary.dark">
                      Tasa anual
                    </Typography>
                    <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                      {annualRateLabel}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="primary.dark">
                      Tipo
                    </Typography>
                    <Typography variant="body2" color="primary.dark" fontWeight={600} textAlign="right">
                      {productSelected.creditTypeLabel}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  borderTop="1px solid #D8E6DF"
                  mt={2.5}
                  pt={2.5}
                  display="flex"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Typography variant="body1" color="primary.dark" fontWeight={700} component="div">
                    Pago mensual estimado*
                  </Typography>
                  <Typography component="div" textAlign="right" color="primary.dark" fontWeight={700}>
                    <Box component="span" sx={{ fontSize: isMobileOrTablet ? "30px" : "34px", lineHeight: 1 }}>
                      {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "$0" : formatCurrency(monthlyRate)}
                    </Box>{" "}
                    <Box component="span" sx={{ fontSize: "16px", fontWeight: 500 }}>
                      MXN
                    </Box>
                  </Typography>
                </Box>
              </Box>

              <Typography textAlign={"center"} fontWeight={300} color="primary.dark" mt={2} mb={2}>
                <small>
                  *Esta simulación es para fines demostrativos y está sujeto a aprobación, las condiciones como la tasa,
                  el monto del crédito, o la comisión por apertura, podrían cambiar dependiendo el perfil y la situación
                  actual del solicitante.
                </small>
              </Typography>

              <Box display="flex" justifyContent="center" mb={2}>
                <Button
                  variant="contained"
                  onClick={setConditions}
                  disabled={isLoading}
                  sx={{ width: "100%", maxWidth: 560, minHeight: 56, borderRadius: "12px" }}
                >
                  Continuar
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default CotizadorSfi;
